import { ReactNode, useCallback, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Dropzone from '../Dropzone/Dropzone';

/** Minimum shape every import endpoint returns; specific responses extend it. */
export interface ImportResultBase {
    rowsParsed: number;
    warnings: string[];
}

interface SummaryRow {
    label: string;
    value: ReactNode;
}

interface ImportDialogProps<TResult extends ImportResultBase> {
    open: boolean;
    onClose: () => void;
    title: string;
    description: string;
    /** Comma-separated accept string passed to the Dropzone. */
    accept: string;
    isImporting: boolean;
    /** Upload one file and resolve with the import result (typically a React Query mutateAsync). */
    importFile: (file: File) => Promise<TResult>;
    /** Per-result summary rows rendered as a small key/value table. */
    summaryRows: (result: TResult) => SummaryRow[];
    /**
     * Whether the import is finished (hides Import, shows Close). Defaults to "any rows parsed",
     * but callers whose result reports write counts should base it on those so an import that
     * parsed rows yet wrote nothing (e.g. all rows skipped) still lets the user retry.
     */
    isComplete?: (result: TResult) => boolean;
    /** Optional action rendered under the description, e.g. a "Download template" button. */
    templateAction?: ReactNode;
}

/**
 * Generic spreadsheet-import dialog: file dropzone, import action, result summary, and warnings.
 * Shared by the Billing Config and On-Top Costs import dialogs, which differ only in copy, accepted
 * file types, the import hook, and which summary rows they show.
 */
function ImportDialog<TResult extends ImportResultBase>({
    open,
    onClose,
    title,
    description,
    accept,
    isImporting,
    importFile,
    summaryRows,
    isComplete,
    templateAction,
}: ImportDialogProps<TResult>) {
    const { t } = useTranslation('budget');
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [result, setResult] = useState<TResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const resetState = useCallback(() => {
        setUploadedFiles([]);
        setResult(null);
        setError(null);
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onClose();
    }, [onClose, resetState]);

    const handleFileUpload = useCallback((updater: (prev: File[]) => File[]) => {
        setUploadedFiles((prev) => updater(prev));
        setResult(null);
        setError(null);
    }, []);

    const handleImport = useCallback(async () => {
        if (uploadedFiles.length === 0) return;
        try {
            const response = await importFile(uploadedFiles[0]);
            setResult(response);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            setError(t('import.failed', { message }));
        }
    }, [uploadedFiles, importFile, t]);

    const isDone = Boolean(result && (isComplete ? isComplete(result) : result.rowsParsed > 0));

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: templateAction ? 1 : 2 }}>
                    {description}
                </Typography>

                {templateAction && <Box sx={{ mb: 2 }}>{templateAction}</Box>}

                <Dropzone
                    accept={accept}
                    multiple={false}
                    uploadedFiles={uploadedFiles}
                    onFileUpload={handleFileUpload}
                    maxSizeInMB={10}
                />

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {result && (
                    <Box sx={{ mt: 2 }}>
                        <Table size="small">
                            <TableBody>
                                {summaryRows(result).map((summaryRow) => (
                                    <TableRow key={summaryRow.label}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{summaryRow.label}</TableCell>
                                        <TableCell>{summaryRow.value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {result.warnings.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                <Box>
                                    {result.warnings.map((w, i) => (
                                        <Typography key={i} variant="body2">
                                            {w}
                                        </Typography>
                                    ))}
                                </Box>
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                    <span>{isDone ? t('action.close') : t('action.cancel')}</span>
                </Button>
                {!isDone && (
                    <Button
                        onClick={handleImport}
                        variant="contained"
                        disabled={uploadedFiles.length === 0 || isImporting}
                    >
                        <span>{isImporting ? t('import.inProgress') : t('action.import')}</span>
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default ImportDialog;
